/**
 * Tenant Resolution Service
 *
 * Resolves the active tenant from:
 * 1. Hostname (e.g., schoolname.resilience.app)
 * 2. Cohort code (e.g., ?cohort=SCHOOL2024)
 * 3. Invite token (e.g., ?invite=abc123)
 * 4. Falls back to default tenant
 */

import type { TenantConfig, TenantResolution } from './types';
import { DEFAULT_TENANT } from './types';

// ============================================
// TENANT REGISTRY (In production, this would be an API)
// ============================================

/**
 * In-memory tenant registry for development
 * In production, this would fetch from an API
 */
const TENANT_REGISTRY: Map<string, TenantConfig> = new Map();

// Hostname to tenant mapping
const HOSTNAME_MAP: Map<string, string> = new Map();

// Cohort code to tenant mapping
const COHORT_MAP: Map<string, string> = new Map();

// Invite token to tenant mapping (with expiry)
const INVITE_MAP: Map<string, { tenantId: string; expiresAt: Date }> = new Map();

// ============================================
// INITIALIZATION
// ============================================

/**
 * Initialize the tenant registry with a list of tenants
 */
export function initializeTenantRegistry(tenants: TenantConfig[]): void {
  TENANT_REGISTRY.clear();
  HOSTNAME_MAP.clear();

  // Always include default tenant
  TENANT_REGISTRY.set(DEFAULT_TENANT.id, DEFAULT_TENANT);

  for (const tenant of tenants) {
    TENANT_REGISTRY.set(tenant.id, tenant);
  }
}

/**
 * Register a hostname mapping
 */
export function registerHostname(hostname: string, tenantId: string): void {
  HOSTNAME_MAP.set(hostname.toLowerCase(), tenantId);
}

/**
 * Register a cohort code mapping
 */
export function registerCohort(cohortCode: string, tenantId: string): void {
  COHORT_MAP.set(cohortCode.toUpperCase(), tenantId);
}

/**
 * Register an invite token (with expiry)
 */
export function registerInviteToken(
  token: string,
  tenantId: string,
  expiresAt: Date
): void {
  INVITE_MAP.set(token, { tenantId, expiresAt });
}

// ============================================
// RESOLUTION STRATEGIES
// ============================================

/**
 * Resolve tenant by hostname
 */
function resolveByHostname(hostname: string): TenantConfig | null {
  const normalizedHostname = hostname.toLowerCase();

  // Check direct mapping
  const tenantId = HOSTNAME_MAP.get(normalizedHostname);
  if (tenantId) {
    return TENANT_REGISTRY.get(tenantId) || null;
  }

  // Check subdomain pattern (e.g., school.resilience.app)
  const parts = normalizedHostname.split('.');
  if (parts.length >= 3) {
    const subdomain = parts[0];
    const subdomainTenantId = HOSTNAME_MAP.get(subdomain);
    if (subdomainTenantId) {
      return TENANT_REGISTRY.get(subdomainTenantId) || null;
    }

    // Also check if tenant slug matches subdomain
    for (const tenant of TENANT_REGISTRY.values()) {
      if (tenant.slug.toLowerCase() === subdomain) {
        return tenant;
      }
    }
  }

  return null;
}

/**
 * Resolve tenant by cohort code
 */
function resolveByCohortCode(cohortCode: string): TenantConfig | null {
  const normalizedCode = cohortCode.toUpperCase();
  const tenantId = COHORT_MAP.get(normalizedCode);

  if (tenantId) {
    return TENANT_REGISTRY.get(tenantId) || null;
  }

  return null;
}

/**
 * Resolve tenant by invite token
 */
function resolveByInviteToken(token: string): TenantConfig | null {
  const invite = INVITE_MAP.get(token);

  if (!invite) {
    return null;
  }

  // Check expiry
  if (new Date() > invite.expiresAt) {
    INVITE_MAP.delete(token); // Clean up expired token
    return null;
  }

  return TENANT_REGISTRY.get(invite.tenantId) || null;
}

// ============================================
// MAIN RESOLVER
// ============================================

/**
 * Resolve the current tenant based on context
 */
export function resolveTenant(): TenantResolution {
  // Skip in SSR/non-browser environments
  if (typeof window === 'undefined') {
    return {
      tenant: DEFAULT_TENANT,
      source: 'default',
    };
  }

  const url = new URL(window.location.href);
  const hostname = url.hostname;

  // 1. Check for invite token in URL
  const inviteToken = url.searchParams.get('invite');
  if (inviteToken) {
    const tenant = resolveByInviteToken(inviteToken);
    if (tenant) {
      // Store in session for future requests
      sessionStorage.setItem('tenant_invite', inviteToken);
      return {
        tenant,
        source: 'invite_token',
        matchedValue: inviteToken,
      };
    }
  }

  // Check session storage for previously used invite
  const storedInvite = sessionStorage.getItem('tenant_invite');
  if (storedInvite) {
    const tenant = resolveByInviteToken(storedInvite);
    if (tenant) {
      return {
        tenant,
        source: 'invite_token',
        matchedValue: storedInvite,
      };
    }
    // Clear expired/invalid invite
    sessionStorage.removeItem('tenant_invite');
  }

  // 2. Check for cohort code in URL
  const cohortCode = url.searchParams.get('cohort');
  if (cohortCode) {
    const tenant = resolveByCohortCode(cohortCode);
    if (tenant) {
      // Store in session for future requests
      sessionStorage.setItem('tenant_cohort', cohortCode);
      return {
        tenant,
        source: 'cohort_code',
        matchedValue: cohortCode,
      };
    }
  }

  // Check session storage for previously used cohort
  const storedCohort = sessionStorage.getItem('tenant_cohort');
  if (storedCohort) {
    const tenant = resolveByCohortCode(storedCohort);
    if (tenant) {
      return {
        tenant,
        source: 'cohort_code',
        matchedValue: storedCohort,
      };
    }
    // Clear invalid cohort
    sessionStorage.removeItem('tenant_cohort');
  }

  // 3. Check hostname
  const tenantByHostname = resolveByHostname(hostname);
  if (tenantByHostname) {
    return {
      tenant: tenantByHostname,
      source: 'hostname',
      matchedValue: hostname,
    };
  }

  // 4. Fall back to default tenant
  return {
    tenant: DEFAULT_TENANT,
    source: 'default',
  };
}

/**
 * Get a specific tenant by ID
 */
export function getTenantById(tenantId: string): TenantConfig | null {
  return TENANT_REGISTRY.get(tenantId) || null;
}

/**
 * Get all registered tenants
 */
export function getAllTenants(): TenantConfig[] {
  return Array.from(TENANT_REGISTRY.values());
}

/**
 * Check if a tenant exists
 */
export function tenantExists(tenantId: string): boolean {
  return TENANT_REGISTRY.has(tenantId);
}

// ============================================
// TENANT FETCHING (Production use)
// ============================================

/**
 * Fetch tenant configuration from API
 * This would be used in production instead of in-memory registry
 */
export async function fetchTenantConfig(
  identifier: string,
  type: 'hostname' | 'cohort' | 'invite' | 'id'
): Promise<TenantConfig | null> {
  // In production, this would call an API endpoint
  // For now, use the in-memory registry
  switch (type) {
    case 'hostname':
      return resolveByHostname(identifier);
    case 'cohort':
      return resolveByCohortCode(identifier);
    case 'invite':
      return resolveByInviteToken(identifier);
    case 'id':
      return TENANT_REGISTRY.get(identifier) || null;
    default:
      return null;
  }
}

/**
 * Fetch tenant from API endpoint
 * For production use with a real backend
 */
export async function fetchTenantFromAPI(
  endpoint: string
): Promise<TenantConfig | null> {
  try {
    const response = await fetch(endpoint);

    if (!response.ok) {
      console.warn('[TenantResolver] Failed to fetch tenant config:', response.status);
      return null;
    }

    const data = await response.json();
    return data as TenantConfig;
  } catch (error) {
    console.error('[TenantResolver] Error fetching tenant config:', error);
    return null;
  }
}

// ============================================
// VALIDATION
// ============================================

/**
 * Validate tenant configuration
 */
export function validateTenantConfig(config: Partial<TenantConfig>): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  // Required fields
  if (!config.id) errors.push('id is required');
  if (!config.slug) errors.push('slug is required');
  if (!config.displayName) errors.push('displayName is required');

  // Brand validation
  if (!config.brand) {
    errors.push('brand configuration is required');
  } else {
    if (!config.brand.name) errors.push('brand.name is required');
    if (!config.brand.shortName) errors.push('brand.shortName is required');
    if (config.brand.shortName && config.brand.shortName.length > 12) {
      errors.push('brand.shortName must be 12 characters or less');
    }
    if (!config.brand.colors) errors.push('brand.colors is required');
    if (!config.brand.logos) errors.push('brand.logos is required');
    if (!config.brand.themeColor) errors.push('brand.themeColor is required');
    if (!config.brand.backgroundColor) errors.push('brand.backgroundColor is required');
  }

  // Slug format
  if (config.slug && !/^[a-z0-9-]+$/.test(config.slug)) {
    errors.push('slug must contain only lowercase letters, numbers, and hyphens');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}
